/**
 * Operators Hooks
 *
 * TanStack Query hooks for operator CRUD operations including:
 * - Basic CRUD with pagination
 * - Photo management (upload, list, delete, set primary)
 * - Face enrollment with AWS Rekognition
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  OperatorListItem,
  OperatorLookup,
  OperatorDetail,
  OperatorPhotoWithUrl,
  CreateOperator,
  UpdateOperator,
  OperatorListQuery,
  PhotoUploadRequest,
  PhotoUploadResponse,
  EnrollFaceRequest,
  EnrollFaceResponse,
  EnrollmentStatus,
  EnrollAllPhotosResponse,
} from '@guild/shared';

// Query keys
export const operatorKeys = {
  all: ['operators'] as const,
  lists: () => [...operatorKeys.all, 'list'] as const,
  list: (filters: OperatorListQuery & { page?: number; limit?: number }) =>
    [...operatorKeys.lists(), filters] as const,
  lookup: () => [...operatorKeys.all, 'lookup'] as const,
  details: () => [...operatorKeys.all, 'detail'] as const,
  detail: (id: string) => [...operatorKeys.details(), id] as const,
  photos: (operatorId: string) => [...operatorKeys.all, 'photos', operatorId] as const,
  enrollmentStatus: (operatorId: string) => [...operatorKeys.all, 'enrollment-status', operatorId] as const,
};

// Response types for paginated data
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// LIST QUERIES
// ============================================

/**
 * Fetch operators list with pagination and filters
 */
export function useOperators(options?: OperatorListQuery & { page?: number; limit?: number }) {
  const api = useApi();

  return useQuery({
    queryKey: operatorKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.departmentId) params.set('departmentId', options.departmentId);
      if (options?.shiftId) params.set('shiftId', options.shiftId);
      if (options?.positionId) params.set('positionId', options.positionId);
      if (options?.stationId) params.set('stationId', options.stationId);
      if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
      if (options?.hasEnrolledFace !== undefined) params.set('hasEnrolledFace', String(options.hasEnrolledFace));
      if (options?.search) params.set('search', options.search);
      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));

      const queryString = params.toString();
      const endpoint = queryString ? `/api/operators?${queryString}` : '/api/operators';

      return api.get<PaginatedResponse<OperatorListItem>>(endpoint);
    },
  });
}

/**
 * Fetch lightweight operators list for dropdowns
 */
export function useOperatorLookup() {
  const api = useApi();

  return useQuery({
    queryKey: operatorKeys.lookup(),
    queryFn: () => api.get<OperatorLookup[]>('/api/operators/lookup'),
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

/**
 * Fetch single operator details
 */
export function useOperator(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: operatorKeys.detail(id ?? ''),
    queryFn: () => api.get<OperatorDetail>(`/api/operators/${id}`),
    enabled: !!id,
  });
}

// ============================================
// CRUD MUTATIONS
// ============================================

/**
 * Create operator mutation
 */
export function useCreateOperator() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOperator) =>
      api.post<OperatorDetail>('/api/operators', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.all });
    },
  });
}

/**
 * Update operator mutation
 */
export function useUpdateOperator() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOperator }) =>
      api.put<OperatorDetail>(`/api/operators/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.lookup() });
    },
  });
}

/**
 * Delete operator mutation (soft delete)
 */
export function useDeleteOperator() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/operators/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.all });
    },
  });
}

// ============================================
// PHOTO MANAGEMENT
// ============================================

/**
 * Fetch operator photos
 */
export function useOperatorPhotos(operatorId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: operatorKeys.photos(operatorId ?? ''),
    queryFn: () => api.get<OperatorPhotoWithUrl[]>(`/api/operators/${operatorId}/photos`),
    enabled: !!operatorId,
  });
}

/**
 * Request presigned URL for photo upload
 */
export function useRequestPhotoUpload() {
  const api = useApi();

  return useMutation({
    mutationFn: ({ operatorId, data }: { operatorId: string; data: PhotoUploadRequest }) =>
      api.post<PhotoUploadResponse & { photoId: string }>(`/api/operators/${operatorId}/photos`, data),
    // Don't invalidate immediately - wait for actual upload
    // User will call refresh after uploading to S3
  });
}

/**
 * Delete operator photo
 */
export function useDeletePhoto() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ operatorId, photoId }: { operatorId: string; photoId: string }) =>
      api.delete<{ id: string }>(`/api/operators/${operatorId}/photos/${photoId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.photos(variables.operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(variables.operatorId) });
    },
  });
}

/**
 * Set photo as primary
 */
export function useSetPrimaryPhoto() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ operatorId, photoId }: { operatorId: string; photoId: string }) =>
      api.put<{ id: string; isPrimary: boolean }>(
        `/api/operators/${operatorId}/photos/${photoId}/primary`,
        {}
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.photos(variables.operatorId) });
    },
  });
}

/**
 * Refresh photos after S3 upload completes
 */
export function useRefreshPhotos() {
  const queryClient = useQueryClient();

  return (operatorId: string) => {
    queryClient.invalidateQueries({ queryKey: operatorKeys.photos(operatorId) });
    queryClient.invalidateQueries({ queryKey: operatorKeys.detail(operatorId) });
  };
}

// ============================================
// FACE ENROLLMENT
// ============================================

/**
 * Enroll operator face with Rekognition
 */
export function useEnrollFace() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ operatorId, data }: { operatorId: string; data: EnrollFaceRequest }) =>
      api.post<EnrollFaceResponse>(`/api/operators/${operatorId}/enroll`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(variables.operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.lists() });
    },
  });
}

/**
 * Remove face enrollment
 */
export function useRemoveFaceEnrollment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operatorId: string) =>
      api.delete<{ message: string }>(`/api/operators/${operatorId}/enroll`),
    onSuccess: (_, operatorId) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.lists() });
    },
  });
}

// ============================================
// PHASE 7: MULTI-PHOTO ENROLLMENT
// ============================================

/**
 * Fetch enrollment status with per-photo details
 */
export function useEnrollmentStatus(operatorId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: operatorKeys.enrollmentStatus(operatorId ?? ''),
    queryFn: () => api.get<EnrollmentStatus>(`/api/operators/${operatorId}/enrollment-status`),
    enabled: !!operatorId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Enroll all unenrolled photos
 */
export function useEnrollAllPhotos() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operatorId: string) =>
      api.post<EnrollAllPhotosResponse>(`/api/operators/${operatorId}/enroll-all`, {}),
    onSuccess: (_, operatorId) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: operatorKeys.photos(operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.enrollmentStatus(operatorId) });
    },
  });
}

/**
 * Remove all face enrollments for an operator
 */
export function useRemoveAllEnrollments() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (operatorId: string) =>
      api.delete<{ removed: number; message: string }>(`/api/operators/${operatorId}/enroll-all`),
    onSuccess: (_, operatorId) => {
      queryClient.invalidateQueries({ queryKey: operatorKeys.detail(operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: operatorKeys.photos(operatorId) });
      queryClient.invalidateQueries({ queryKey: operatorKeys.enrollmentStatus(operatorId) });
    },
  });
}

// ============================================
// UTILITY: Upload photo to S3
// ============================================

/**
 * Upload a file directly to S3 using presigned URL
 */
export async function uploadToS3(uploadUrl: string, file: File): Promise<boolean> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    return response.ok;
  } catch (error) {
    console.error('S3 upload failed:', error);
    return false;
  }
}
